/**
 * The vfx pool (ADR-134 decision 5): a fixed number of running vfx on a page, each in a slot that holds the images it
 * draws with. A vfx is started for a character (its handle) and a definition (its index); the same pair starting again
 * restarts its own slot; a different pair takes a free slot, and with none free the slot of the vfx started longest ago.
 * The slot's item is made the first time the slot is used and kept, so a page that never plays a vfx holds none.
 */

/**
 * The most vfx running on a page at once, the author's cap for the level-up.
 */
export const VFX_CAP = 8;

const FREE = -1;

/**
 * The pool.
 *
 * @example
 * ```ts
 * const pool = new VfxPool(() => makeImages());
 * const images = pool.start(handle, definitionIndex, now);
 * ```
 */
export class VfxPool<T> {
  private readonly items: (T | undefined)[];
  private readonly handles: Int32Array;
  private readonly keys: Int32Array;
  private readonly starts: Float64Array;
  private made = 0;
  private held = 0;

  /**
   * @param create Makes an item, when a vfx starts and no made item is free and fewer than `size` exist.
   * @param size The most items the pool makes and the most vfx it runs; `VFX_CAP` by default.
   */
  public constructor(
    private readonly create: () => T,
    private readonly size: number = VFX_CAP,
  ) {
    this.items = new Array<T | undefined>(size).fill(undefined);
    this.handles = new Int32Array(size).fill(FREE);
    this.keys = new Int32Array(size).fill(FREE);
    this.starts = new Float64Array(size);
  }

  /**
   * Vfx running now.
   *
   * @returns The count.
   */
  public get active(): number {
    return this.held;
  }

  /**
   * Items made, at most the pool's size.
   *
   * @returns The count.
   */
  public get created(): number {
    return this.made;
  }

  /**
   * Starts a vfx for a character: its own slot if the same vfx runs on it, else a free slot, else the oldest.
   *
   * @param handle The character's handle, 0 or more.
   * @param key The vfx's index in the registry, 0 or more.
   * @param at The time it starts, in ms.
   * @returns The slot's item.
   * @throws RangeError when the handle or the key is not a whole number of 0 or more.
   */
  public start(handle: number, key: number, at: number): T {
    if (!Number.isInteger(handle) || handle < 0 || !Number.isInteger(key) || key < 0) {
      throw new RangeError(`a vfx needs a whole handle and key of 0 or more, not ${handle} and ${key}`);
    }
    let slot = this.slotOf(handle, key);
    if (slot === FREE) {
      slot = this.freeSlot();
      if (slot === FREE) {
        slot = this.oldestSlot();
      } else {
        this.held += 1;
      }
    }
    this.handles[slot] = handle;
    this.keys[slot] = key;
    this.starts[slot] = at;
    let item = this.items[slot];
    if (item === undefined) {
      item = this.create();
      this.items[slot] = item;
      this.made += 1;
    }
    return item;
  }

  /**
   * Visits the running vfx of one character, in slot order, allocating nothing.
   *
   * @param handle The character's handle.
   * @param visit Told each item, the vfx's index and when it started.
   */
  public forEachOf(handle: number, visit: (item: T, key: number, startedAt: number) => void): void {
    for (let slot = 0; slot < this.size; slot += 1) {
      if (this.handles[slot] === handle) {
        visit(this.items[slot] as T, this.keys[slot] as number, this.starts[slot] as number);
      }
    }
  }

  /**
   * Visits every running vfx, in slot order. A visit may end the vfx it is told of.
   *
   * @param visit Told each item, its character's handle, the vfx's index, when it started and its slot.
   */
  public forEachActive(visit: (item: T, handle: number, key: number, startedAt: number, slot: number) => void): void {
    for (let slot = 0; slot < this.size; slot += 1) {
      const handle = this.handles[slot] as number;
      if (handle !== FREE) {
        visit(this.items[slot] as T, handle, this.keys[slot] as number, this.starts[slot] as number, slot);
      }
    }
  }

  /**
   * Ends the vfx in a slot, freeing it.
   *
   * @param slot The slot from `forEachActive`.
   */
  public end(slot: number): void {
    if (this.handles[slot] !== FREE) {
      this.handles[slot] = FREE;
      this.keys[slot] = FREE;
      this.held -= 1;
    }
  }

  /**
   * Ends every vfx, as a scene's end must.
   */
  public clear(): void {
    this.handles.fill(FREE);
    this.keys.fill(FREE);
    this.held = 0;
  }

  /**
   * The slot a character's vfx runs in.
   *
   * @param handle The handle.
   * @param key The vfx's index.
   * @returns The slot, or -1.
   */
  private slotOf(handle: number, key: number): number {
    for (let slot = 0; slot < this.size; slot += 1) {
      if (this.handles[slot] === handle && this.keys[slot] === key) {
        return slot;
      }
    }
    return FREE;
  }

  /**
   * A free slot, one that has an item first so none is made while one waits.
   *
   * @returns The slot, or -1 when none is free.
   */
  private freeSlot(): number {
    let empty = FREE;
    for (let slot = 0; slot < this.size; slot += 1) {
      if (this.handles[slot] === FREE) {
        if (this.items[slot] !== undefined) {
          return slot;
        }
        if (empty === FREE) {
          empty = slot;
        }
      }
    }
    return empty;
  }

  /**
   * The slot of the vfx started longest ago.
   *
   * @returns The slot.
   */
  private oldestSlot(): number {
    let oldest = 0;
    for (let slot = 1; slot < this.size; slot += 1) {
      if ((this.starts[slot] as number) < (this.starts[oldest] as number)) {
        oldest = slot;
      }
    }
    return oldest;
  }
}
